import { useCallback, useMemo, useRef } from 'react';
import { keepPreviousData, useQueries } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import AssetManagerService, { ASSET_LIST_PAGE_SIZE } from '../services/AssetManager/assetManager.service';
import { AssetSortOrder } from '../enums/assetSortOrder';
import type { AssetKey, AssetListItem, AssetsPage } from '../types/InfrastructureManagerTypes';
import type { VisibleRange } from './useVirtualList';

interface UseAssetListOptions {
  configVersion: string | null;
  isConfigured: boolean;
  statisticId: number | null;
  search: string;
  sort: AssetSortOrder;
  range: VisibleRange;
}

// Only the pages under the visible rows (plus one either side) are observed; pages scrolled away are garbage-collected by their gcTime.
export const useAssetList = ({ configVersion, isConfigured, statisticId, search, sort, range }: UseAssetListOptions) => {
  // An active statistic is a dataset of its own, even on a layer outside the list configuration (story S9).
  const hasDataset = isConfigured || statisticId !== null;
  const { getAssetsPage } = AssetManagerService();

  // Sticky per dataset, so a deep scroll keeps its count while the far pages load.
  const datasetKey = `${configVersion}|${statisticId}|${search}|${sort}`;
  const totalRef = useRef<{ key: string; total: number } | null>(null);
  const storedTotal = totalRef.current?.key === datasetKey ? totalRef.current.total : undefined;

  // One page of buffer each side, so ordinary scrolling reaches rows that are already loaded.
  const lastAvailablePage = storedTotal === undefined ? 0 : Math.max(0, Math.ceil(storedTotal / ASSET_LIST_PAGE_SIZE) - 1);
  const firstPage = Math.max(0, Math.floor(Math.max(range.start, 0) / ASSET_LIST_PAGE_SIZE) - 1);
  const lastPage = Math.max(firstPage, Math.min(lastAvailablePage, Math.floor(Math.max(range.end, 0) / ASSET_LIST_PAGE_SIZE) + 1));
  const pageIndices = useMemo(() => Array.from({ length: lastPage - firstPage + 1 }, (_, offset) => firstPage + offset), [firstPage, lastPage]);

  const combine = useCallback(
    (results: UseQueryResult<AssetsPage, Error>[]) => {
      const pages = new Map<number, AssetListItem[]>();
      let total: number | undefined;
      let placeholderTotal: number | undefined;
      results.forEach(({ data, isPlaceholderData }, position) => {
        if (!data) return;
        // A placeholder's count is shown but never stored as this dataset's total.
        pages.set(pageIndices[position], data.assets);
        if (isPlaceholderData) placeholderTotal = data.total;
        else total = data.total;
      });
      return {
        pages,
        total,
        placeholderTotal,
        isLoading: results.some(({ isLoading }) => isLoading),
        isFetching: results.some(({ isFetching }) => isFetching),
        isLoadingError: results.some(({ isLoadingError }) => isLoadingError),
      };
    },
    [pageIndices],
  );

  const { pages, total, placeholderTotal, isLoading, isFetching, isLoadingError } = useQueries({
    queries: pageIndices.map((pageIndex) => ({
      ...getAssetsPage({ configVersion, statisticId, search, sort, pageIndex }),
      enabled: hasDataset,
      // Keeps the count steady while a new search's first page loads, so the list doesn't collapse.
      placeholderData: pageIndex === 0 ? keepPreviousData : undefined,
    })),
    combine,
  });

  if (total !== undefined) totalRef.current = { key: datasetKey, total };
  const knownTotal = totalRef.current?.key === datasetKey ? totalRef.current.total : placeholderTotal;

  const getAsset = useCallback((index: number) => pages.get(Math.floor(index / ASSET_LIST_PAGE_SIZE))?.[index % ASSET_LIST_PAGE_SIZE], [pages]);

  // Only loaded pages can be searched; a BE position endpoint could resolve the rest later.
  const findAssetIndex = useCallback(
    ({ layerId, keyValue }: AssetKey) => {
      for (const [pageIndex, assets] of pages) {
        const offset = assets.findIndex((asset) => asset.layerId === layerId && asset.keyValue === keyValue);
        if (offset >= 0) return pageIndex * ASSET_LIST_PAGE_SIZE + offset;
      }
      return -1;
    },
    [pages],
  );

  return {
    total: hasDataset ? (knownTotal ?? 0) : 0,
    getAsset,
    findAssetIndex,
    isLoadingAssets: hasDataset && knownTotal === undefined && isLoading,
    isFetchingAssets: isFetching,
    isAssetsLoadingError: isLoadingError,
  };
};
